import { ChangeEvent } from 'react';
import { Formik, Form, FormikProps, FastField } from 'formik';
import { sortedGroups } from '@/pages/CandidateInfo/candidateInfoFields';
import { EditableCandidate } from '@/services/candidates';
import DropDown from '@/trio-ui/Dropdown';
import DropdownGroup from '@/trio-ui/DropdownGroup';
import InputRating from '@/trio-ui/InputRating';
import InputText from '@/trio-ui/InputText';
import { QuestionType, Question } from '@/types';
import { buildErrorMessage } from '@/utils/formsValidation';
import * as Styles from './CandidateOverviewForm.styles';

type CandidateOverviewFormProps = {
  touchedFields: string[];
  errorMessages: Record<string, string>;
  questions: Question[];
  candidate: EditableCandidate;
  onSubmit: (candidateToUpdate: EditableCandidate) => Promise<void>;
  onChangeHandler: (
    form: FormikProps<EditableCandidate>,
    field: Question,
    event: ChangeEvent<HTMLInputElement>,
    submitForm?: boolean
  ) => void;
};

const CandidateOverviewForm = ({
  questions,
  candidate,
  onSubmit,
  onChangeHandler,
  touchedFields,
  errorMessages,
}: CandidateOverviewFormProps): JSX.Element => {
  const renderRatingField = ({
    field,
    value,
    errorMessage,
    handleChange,
  }: {
    field: Question;
    value: any;
    errorMessage: string;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  }): JSX.Element => {
    return (
      <Styles.InputWrapper key={field.name}>
        <InputRating
          hideInput
          name={field.name}
          label={field.label}
          value={value}
          disabled
          onChangeHandler={handleChange}
          errorMessage={errorMessage}
        />
      </Styles.InputWrapper>
    );
  };

  const renderDropdownGroupField = ({
    field,
    value,
    errorMessage,
    handleChange,
  }: {
    field: Question;
    value: any;
    errorMessage: string;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  }): JSX.Element => {
    return (
      <Styles.InputWrapper key={field.name}>
        <DropdownGroup
          isDisabled={field?.isDisabled}
          dropdownGroups={field.dropdownGroups || []}
          name={field.name}
          defaultValue={value}
          label={field.label}
          errorMessage={errorMessage}
          onChangeDropDown={handleChange}
        />
      </Styles.InputWrapper>
    );
  };

  const renderDropdownField = ({
    field,
    value,
    errorMessage,
    handleChange,
  }: {
    field: Question;
    value: any;
    errorMessage: string;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  }): JSX.Element => {
    return (
      <Styles.InputWrapper key={field.name}>
        <DropDown
          optionsWithLabel={field?.optionsWithLabel}
          isDisabled={field?.isDisabled}
          isMultiple={field.isMultiple}
          options={field.options ?? []}
          name={field.name}
          defaultValue={value}
          label={field.label}
          errorMessage={errorMessage}
          onChangeDropDown={handleChange}
        />
      </Styles.InputWrapper>
    );
  };

  const renderTextField = ({
    field,
    value,
    errorMessage,
    handleChange,
    handleSubmit,
  }: {
    field: Question;
    value: any;
    errorMessage: string;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleSubmit: (event: any) => void;
  }): JSX.Element => {
    return (
      <Styles.InputWrapper key={field.name}>
        <InputText
          onChangeHandler={handleChange}
          onBlurHandler={handleSubmit}
          label={field.label}
          isDisabled={field.isDisabled}
          name={field.name}
          value={value}
          errorMessage={errorMessage}
        />
      </Styles.InputWrapper>
    );
  };

  const renderTagSelectorField = ({
    field,
    value,
    errorMessage,
    form,
    handleChange,
  }: {
    field: Question;
    value: any;
    errorMessage: string;
    form: FormikProps<EditableCandidate>;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  }): JSX.Element => {
    const isFullStack = form.values?.developerType?.includes('Full Stack');
    const tagSelectorLimit = isFullStack ? 6 : 3;

    return (
      <Styles.InputWrapper key={field.name}>
        <Styles.CustomTagSelector
          isDisabled
          disableDeleteAll
          optionsWithLabel={field?.optionsWithLabel}
          selectorLimit={tagSelectorLimit}
          name={field.name}
          defaultValue={value}
          label={field.label}
          errorMessage={errorMessage}
          onChangeDropDown={handleChange}
        />
      </Styles.InputWrapper>
    );
  };

  return (
    <div>
      <Formik initialValues={candidate} onSubmit={onSubmit}>
        {(form) => {
          const isExternalSourced = ['Employee Referral', 'External Referral'].includes(
            form?.values?.source
          );

          return (
            <Form onSubmit={form.handleSubmit}>
              {sortedGroups.map((groups, index) => {
                return (
                  <Styles.FormGroup key={index.toString()}>
                    {groups.map((group, subIndex) => {
                      const field = questions.find((question) => question.name === group);
                      const isReferredByField = field?.name === 'referredBy';

                      if (!field || (isReferredByField && !isExternalSourced)) {
                        return <div key={`${index.toString()}-${subIndex.toString()}-child`} />;
                      }
                      const onBlur = form.handleSubmit;
                      const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
                        onChangeHandler(form, field, event, field.type !== QuestionType.text);
                      };

                      const errorMessage = buildErrorMessage(
                        touchedFields.includes(field.name),
                        errorMessages[field.name]
                      );

                      if (field.type === QuestionType.rating) {
                        return (
                          <FastField name={field.name} key={field.name}>
                            {({ field: formikField }) =>
                              renderRatingField({
                                field,
                                value: formikField.value,
                                errorMessage,
                                handleChange,
                              })
                            }
                          </FastField>
                        );
                      }

                      if (field.dropdownGroups) {
                        return (
                          <FastField name={field.name} key={field.name}>
                            {({ field: formikField }) =>
                              renderDropdownGroupField({
                                field,
                                value: formikField.value,
                                errorMessage,
                                handleChange,
                              })
                            }
                          </FastField>
                        );
                      }

                      if (field.type === QuestionType.dropdown && field.name === 'skills') {
                        return renderTagSelectorField({
                          field,
                          form,
                          value: candidate.skills,
                          errorMessage,
                          handleChange,
                        });
                      }

                      if (field.type === QuestionType.dropdown) {
                        const isTalentPoolDropdown = field.name === 'isTalentPool';

                        return (
                          <FastField name={field.name} key={field.name}>
                            {({ field: formikField }) => {
                              let actualValue = formikField.value;

                              if (isTalentPoolDropdown) {
                                actualValue = formikField.value ? 'Yes' : 'No';
                              }

                              return renderDropdownField({
                                field,
                                value: actualValue,
                                errorMessage,
                                handleChange,
                              });
                            }}
                          </FastField>
                        );
                      }

                      if (field.type === QuestionType.text) {
                        return (
                          <FastField name={field.name} key={field.name}>
                            {({ field: formikField }) => {
                              return renderTextField({
                                field,
                                value: formikField.value || '',
                                errorMessage,
                                handleChange,
                                handleSubmit: onBlur,
                              });
                            }}
                          </FastField>
                        );
                      }

                      return <div key={field.name} />;
                    })}
                  </Styles.FormGroup>
                );
              })}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CandidateOverviewForm;
